import React, { useCallback, useState } from 'react';
import { withFormik, FormikProps } from 'formik';
import Modal from '../../../components/modal/rightSlideInModal';
import SearchForm from './components/searchForm';
import Store from './components/store';
import ajaxRequest from '../../../helpers/ajaxRequest';
import { API, ReserveCollectSearchResultStore } from '../../../types';
import { MessagePropertiesContext } from '../../../contexts/messageProperties';
import { addToCartHandler, updateMiniCart } from '../../../helpers/addToCartHandler';
import { formFieldsValidation } from '../../../helpers/formFieldsValidation';
import { getUniqueRadioValue, getSelectedVariantCode, checkVariantSelectionValidity } from './reserveCollectUtils';

const { productCode, reserveCollectValidationRules, isRingProduct, priceUponEnquiry } = window.globalProductDetails;

interface ReserveCollectValues {
  reserveCollectSearch: string;
  storesWithStock: boolean;
}

interface SearchResultsRef {
  results: ReserveCollectSearchResultStore[];
  resultsWithStock: ReserveCollectSearchResultStore[];
}

const initialValues = {
  reserveCollectSearch: '',
  storesWithStock: false
};

function ReserveCollectApp({
  values,
  validateForm,
  handleChange,
  resetForm,
  setTouched
}: FormikProps<ReserveCollectValues>): React.ReactElement {
  const INITIAL_SEARCH_RESULTS: SearchResultsRef = { results: [], resultsWithStock: [] };

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [buttonIsLoading, setButtonIsLoading] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchProductCode, setSearchProductCode] = React.useState('');
  const [serverError, setServerError] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedOption, setSelectedOption] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<ReserveCollectSearchResultStore[]>([]);
  const [store, setSelectedStore] = useState<ReserveCollectSearchResultStore>();
  const [formattedMessage] = React.useContext(MessagePropertiesContext);

  const searchResultsRef = React.useRef<SearchResultsRef>(INITIAL_SEARCH_RESULTS);
  const variantSelectorElementRef = React.useRef<Element | null>(null);
  const variantSelectorChangeHandlerRef = React.useRef<(e: Event) => void>(() => {});
  const [assignedVariantListener, setAssignedVariantListener] = React.useState(false);

  const modalHeading = formattedMessage({
    id: 'product.arrange.consultation.reserve.main',
    fallback: 'Arrange a Consultation'
  });

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement | HTMLButtonElement> | Event): void => {
    e.preventDefault();

    const variantSelector: HTMLFormElement | null = document.querySelector('form.variant-select');
    const productCodeToSearch = (variantSelector && getSelectedVariantCode(variantSelector)) || productCode;
    const shouldValidateVariantSelector = isRingProduct === false;
    const errorMessage = formattedMessage({
      id: 'reserve.and.collect.empty.variant.error',
      fallback: 'Please select variant'
    });
    checkVariantSelectionValidity(variantSelector, errorMessage, shouldValidateVariantSelector)
      .then(() => addVariantSelectionListener(variantSelector))
      .then(() => searchReservationStores(productCodeToSearch))
      .catch(() => {
        setIsModalOpen(false);
      });

    function addVariantSelectionListener(variantSelectorElement: Element | null): void {
      if (variantSelectorElement && !assignedVariantListener) {
        variantSelectorElementRef.current = variantSelectorElement;
        variantSelectorChangeHandlerRef.current = handleSubmit;
        variantSelectorElementRef.current.addEventListener('change', variantSelectorChangeHandlerRef.current);
        setAssignedVariantListener(true);
      }
    }

    function searchReservationStores(sProductCode: string): void {
      setTouched(Object.keys(initialValues).reduce((acc, curr) => ({ ...acc, [curr]: true }), {}));
      validateForm().then((errors) => {
        if (Object.keys(errors).length > 0) {
          return;
        }
        setIsSearching(true);
        ajaxRequest({
          method: 'GET',
          url: API.PRODUCT_RESERVATION,
          data: {
            product: sProductCode,
            location: values.reserveCollectSearch
          }
        })
          .then((response) => {
            if (isErrorResponse(response)) {
              return;
            }
            setSearchProductCode(sProductCode);
            setSearchTerm(response.data.storeData.formattedSearchTerm);
            setSearchResults(response.data.storeData.stores);
            searchResultsRef.current.results = response.data.storeData.stores;
            setSelectedOption(getSelectionFirstStore(response.data.storeData.stores[0]));
          })
          .catch(handleSearchGenericError)
          .finally(() => {
            setIsSearching(false);
          });
      });
    }
  };

  function isErrorResponse(response: any): boolean {
    if (response.status === 'ERROR') {
      setServerError(response.message);
      searchReset();
      return true;
    }
    return false;
  }

  function handleSearchGenericError(): void {
    setServerError(
      formattedMessage({
        id: 'reserve.and.collect.genericError',
        fallback: 'Sorry, an error occurred, please try again'
      })
    );
    searchReset();
  }

  function getSelectionFirstStore(firstStore: ReserveCollectSearchResultStore): string {
    if (firstStore.instoreAvailability.available) {
      return getUniqueRadioValue(firstStore, 'instore');
    }
    if (firstStore.onlineAvailability.available) {
      return getUniqueRadioValue(firstStore, 'online');
    }
    return '';
  }

  function handleFormReset(): void {
    resetForm();
    setSearchTerm('');
    searchReset();
  }

  function searchReset(): void {
    setSearchProductCode('');
    setSelectedOption('');
    setSearchResults([]);
    searchResultsRef.current = INITIAL_SEARCH_RESULTS;

    if (assignedVariantListener && variantSelectorElementRef.current) {
      variantSelectorElementRef.current.removeEventListener('change', variantSelectorChangeHandlerRef.current);
      setAssignedVariantListener(false);
    }
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleChange(e);
    if (serverError) {
      setServerError('');
    }
  };

  function getReservationUrl(): string {
    return `${API.RESERVATION_PAGE}
      ${
        isRingProduct && store?.availableInstoreProduct.code !== searchProductCode
          ? `?product=${store?.availableInstoreProduct.code}&intendedProduct=${searchProductCode}`
          : `?product=${searchProductCode}`
      }
      &store=${store?.store.storeId}`.replace(/\n\s+/g, '');
  }

  async function handleAddToCart(): Promise<void> {
    const variantSelector: HTMLSelectElement | null = document.querySelector('.variant-select');
    const errorMessage = formattedMessage({
      id: 'reserve.and.collect.addToBag.error',
      fallback: 'Please select a size before adding to your bag'
    });

    return checkVariantSelectionValidity(variantSelector, errorMessage)
      .then(submitAddToCart)
      .catch(() => {
        setIsModalOpen(false);
      });

    async function submitAddToCart(): Promise<void> {
      const formElement = document.querySelector('.add_to_cart_form');
      if (formElement) {
        setButtonIsLoading(true);
        addToCartHandler(formElement as HTMLFormElement)
          .then(updateMiniCart)
          .catch(() => {})
          .finally(() => {
            setButtonIsLoading(false);
            setIsModalOpen(false);
          });
      }
    }
  }

  const CTA = useCallback(() => {
    let instoreUniqueValue;
    let onlineUniqueValue;

    if (store) {
      instoreUniqueValue = getUniqueRadioValue(store, 'instore');
      onlineUniqueValue = getUniqueRadioValue(store, 'online');
    }

    const ReserveCTA = ({ isDisabled }: { isDisabled?: boolean }): React.ReactElement => (
      <span className={`${isDisabled ? 'reserve-collect-store__disabled-wrapper' : ''}`}>
        <a
          href={getReservationUrl()}
          className={`button--primary reserve-collect-store__options-item-cta 
          ${isDisabled ? 'reserve-collect-store__options-item-cta--disabled' : ''}
          `}
        >
          {formattedMessage({ id: 'reserve.and.collect.reserveHere', fallback: 'Reserve here' })}
        </a>
      </span>
    );

    const AddToBagCTA = (): React.ReactElement => (
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={buttonIsLoading}
        className={`button--primary reserve-collect-store__options-item-cta ${
          buttonIsLoading ? 'reserve-collect-store__options-item-cta--loading' : ''
        }`}
      >
        <span className={`loader ${buttonIsLoading ? '' : 'hide'}`} />
        {formattedMessage({ id: 'reserve.and.collect.addToBag', fallback: 'Add to bag' })}
      </button>
    );

    return (
      <>
        {selectedOption === instoreUniqueValue ? (
          <ReserveCTA />
        ) : selectedOption === onlineUniqueValue && !priceUponEnquiry ? (
          <AddToBagCTA />
        ) : (
          <ReserveCTA isDisabled />
        )}
      </>
    );
  }, [selectedOption, store, getReservationUrl, buttonIsLoading, handleAddToCart]);

  const handleClose = (): void => setIsModalOpen(false);
  const handleOpen = (): void => setIsModalOpen(true);

  return (
    <Modal
      onOpen={handleOpen}
      secondFooterButton={<CTA />}
      isOpen={isModalOpen}
      onClose={handleClose}
      heading={modalHeading}
    >
      <div className="reserve-collect">
        <SearchForm
          searchResults={searchResults}
          handleSubmit={handleSubmit}
          handleOnChange={handleOnChange}
          serverError={serverError}
          isSearching={isSearching}
          searchTerm={values.reserveCollectSearch}
        />

        {searchResults.length > 0 && (
          <>
            <div className="reserve-collect__search-result">
              <div
                className="reserve-collect__search-result-location"
                dangerouslySetInnerHTML={{
                  __html: formattedMessage({
                    id: 'reserve.and.collect.location.title',
                    fallback: `Your location: <span>${searchTerm}</span>`,
                    values: {
                      0: searchTerm
                    }
                  })
                }}
              />
              <button
                type="button"
                className="button--underline reserve-collect__search-result-change"
                onClick={handleFormReset}
              >
                <span className="button--underline-text-blue">
                  {formattedMessage({ id: 'reserve.and.collect.change', fallback: 'Change' })}
                </span>
              </button>
            </div>
            <ul className="reserve-collect__stores-list">
              {searchResults.map((storeData) => (
                <li key={storeData.store.storeId} className="reserve-collect__stores-list-item">
                  <Store
                    store={storeData}
                    disabled={isSearching}
                    isRingProduct={isRingProduct}
                    searchProductCode={searchProductCode}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    setSelectedStore={setSelectedStore}
                    priceUponEnquiry={priceUponEnquiry}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  );
}

export default withFormik({
  mapPropsToValues: (): ReserveCollectValues => initialValues,
  validate: (values: ReserveCollectValues) => formFieldsValidation(values, reserveCollectValidationRules),
  handleSubmit: () => {},
  displayName: 'ReserveCollectForm'
})(ReserveCollectApp);
